/**
 * @file sync-run-summary.tsx
 * @module app/modules/uProcure/inventory-replenishment/sync-run-summary
 * @description The latest draft PO job run (daily or Sync now) in a few lines: running, what it
 *   changed, or why it stopped (failed, interrupted, cancelled and rolled back). Used in the
 *   Inventory Replenishment Report sync bar and on the Draft PO tab.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 1.4.0
 */

import Method from "../../../../utils/methods";

export interface SyncSummary {
  /** sales dates the run used (Settings: Report range days, ending yesterday) */
  range?: { startDay: string; endDay: string; totalDays: number };
  productsToOrder?: number;
  /** products needing an order that got no new line: already on an open draft / Draft PO switched off */
  skipped?: { draftPoOff: number; alreadyOnDraft: number };
  /** lines moved to their supplier group's draft */
  linesRegrouped?: number;
  draftsUpdated?: string[];
  coveredByOpenPo?: number;
  linesAdded: number;
  linesRefreshed: number;
  linesRemoved: number;
  draftsCreated: string[];
  draftsEmptied: string[];
}

export interface RunInfo {
  status: "running" | "done" | "failed" | "cancelled";
  trigger: "daily" | "manual";
  by: string;
  startedAt: string | null;
  finishedAt: string | null;
  summary: SyncSummary | null;
  error: string;
  /** cancelled run: drafts put back (restored = how many; null while the rollback runs) */
  rolledBack?: boolean;
  restored?: number | null;
}

const time = (iso: string | null) => (iso ? Method.convertDateToFormat(new Date(iso), "DD/MM/YYYY HH:mm") : "-");
const who = (run: RunInfo) => (run.trigger === "manual" ? `Sync now by ${run.by || "a user"}` : "Daily job");
const list = (refs: string[] = [], max = 10) => `${refs.slice(0, max).join(", ")}${refs.length > max ? ` and ${refs.length - max} more` : ""}`;

/** One-line headline for a run, e.g. for the Draft PO tab. */
export const runHeadline = (run: RunInfo): string => {
  if (run.status === "running") return `${who(run)} is running (started ${time(run.startedAt)}). Draft POs may change.`;
  if (run.status === "cancelled")
    return `${who(run)} was cancelled at ${time(run.finishedAt)}${run.error ? ` (${run.error.replace(/^Cancelled by /, "by ")})` : ""}: ${
      run.rolledBack ? `rolled back, ${run.restored ?? 0} draft(s) put back as they were` : "rolling back its changes..."
    }`;
  if (run.status === "failed") return `${who(run)} stopped at ${time(run.finishedAt)}: ${run.error || "failed"}`;
  const s = run.summary;
  return `${who(run)} synced draft POs at ${time(run.finishedAt)}${
    s ? `: ${s.linesAdded} added, ${s.linesRefreshed} updated, ${s.linesRemoved} removed` : ""
  }`;
};

const day = (d: string) => d.split("-").reverse().join("/");

/** Why a finished run added nothing (or little): what it looked at and where the products went. */
export const runReasons = (s: SyncSummary): string[] => {
  const out: string[] = [];
  if (s.range) out.push(`Used sales from ${day(s.range.startDay)} to ${day(s.range.endDay)} (${s.range.totalDays} days, Settings > Report range).`);
  if (typeof s.productsToOrder === "number") {
    if (s.productsToOrder === 0) {
      out.push("No product needs ordering for these dates: stock and open POs cover the sales, or there were no sales in these dates.");
    } else {
      const k = s.skipped ?? { alreadyOnDraft: 0, draftPoOff: 0 };
      const parts = [
        s.linesAdded ? `${s.linesAdded} added to draft POs` : "",
        k.alreadyOnDraft ? `${k.alreadyOnDraft} already on an open draft PO (kept, quantities refreshed when nobody edited them)` : "",
        k.draftPoOff ? `${k.draftPoOff} with Draft PO switched off for the product` : "",
      ].filter(Boolean);
      out.push(`${s.productsToOrder} product(s) need ordering: ${parts.join("; ")}.`);
    }
  }
  if (s.linesRegrouped) out.push(`${s.linesRegrouped} line(s) moved to their supplier group's draft.`);
  return out;
};

/** The run as a short coloured summary box. */
const SyncRunSummary = ({ run }: { run: RunInfo }) => {
  const tone = run.status === "done" ? "success" : run.status === "running" ? "primary" : run.status === "cancelled" ? "warning" : "danger";
  const s = run.status === "done" ? run.summary : null;
  return (
    <div className={`mt-2 p-3 rounded bg-light-${tone} fs-13`}>
      <div className="fw-bold">{runHeadline(run)}</div>
      {s && (
        <>
          {runReasons(s).map((r) => (
            <div key={r} data-testid="sync-reason">
              {r}
            </div>
          ))}
          {typeof s.coveredByOpenPo === "number" && s.coveredByOpenPo > 0 && <div>{s.coveredByOpenPo} product(s) covered by open POs (no draft line)</div>}
          {s.draftsCreated.length > 0 && <div>New drafts: {list(s.draftsCreated)}</div>}
          {(s.draftsUpdated ?? []).length > 0 && <div>Drafts changed: {list(s.draftsUpdated)}</div>}
          {s.draftsEmptied.length > 0 && <div>Emptied (deleted): {list(s.draftsEmptied)}</div>}
        </>
      )}
    </div>
  );
};

export default SyncRunSummary;
